// store/customersSlice.ts
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { RootState } from '@/store/store';
import { customerService } from '@/api/services/customerService';
import type { Contact } from '@/types/contact';

// Define the state structure for the customers slice
interface CustomersState {
    customerMappedById: Record<string, Contact>;
    loading: Record<string, boolean>;
    error: Record<string, string | null>;
}

// Initial state
const initialState: CustomersState = {
    customerMappedById: {},
    loading: {},
    error: {}
};

// Async thunk to fetch a customer by ID (only if not already cached)
export const fetchCustomerById = createAsyncThunk(
    'customers/fetchCustomerById',
    async (id: string, { rejectWithValue }) => {
        try {
            const response = await customerService.fetchCustomerById(id);
            if (response.status !== 'success') {
                return rejectWithValue('Failed to fetch customer data');
            }
            return { id, data: response.data };
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to fetch customer data');
        }
    },
    {
        condition: (id: string, { getState }) => {
            const state = getState() as RootState;
            const alreadyFetched = !!state.customers.customerMappedById[id];
            const isLoading = !!state.customers.loading[id];
            // Skip fetch if already present or in progress
            if (alreadyFetched || isLoading) return false;
        }
    }
);

// Slice
const customersSlice = createSlice({
    name: 'customers',
    initialState,
    reducers: {
        updateCustomer: (state, action) => {
            const { id } = action.payload;
            // Check if the customer is in the state if yes then update otherwise ignore
            if (state.customerMappedById[id]) {
                // Loop over every key in the action.payload and update the customerMappedById
                Object.keys(action.payload).forEach(key => {
                    if (key === 'id') {
                        return;
                    }
                    state.customerMappedById[id][key] = action.payload[key];
                });
            }
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchCustomerById.pending, (state, action) => {
                const id = action.meta.arg;
                state.loading[id] = true;
                state.error[id] = null;
            })
            .addCase(fetchCustomerById.fulfilled, (state, action) => {
                const { id, data } = action.payload;
                state.loading[id] = false;
                state.customerMappedById[id] = data;
            })
            .addCase(fetchCustomerById.rejected, (state, action) => {
                const id = action.meta.arg;
                state.loading[id] = false;
                state.error[id] = action.payload as string;
            });
    }
});

export const { updateCustomer } = customersSlice.actions;

// Selectors
export const selectCustomerById = (state: RootState, id: string) =>
    state.customers.customerMappedById[id];

export const selectIsCustomerLoading = (state: RootState, id: string) =>
    !!state.customers.loading[id];

export const selectCustomerError = (state: RootState, id: string) =>
    state.customers.error[id];

export default customersSlice.reducer;
